import type { ButtonProps as AntButtonProps } from 'antd';
import { Button as AntButton } from 'antd';
import * as React from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant =
  | 'default'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'link';
export type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

export interface ButtonProps
  extends Omit<AntButtonProps, 'size' | 'type' | 'variant'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean; // 保留兼容性，但不再使用
}

// 映射 variant 到 antd type 和样式
const getAntButtonType = (variant: ButtonVariant): AntButtonProps['type'] => {
  switch (variant) {
    case 'default':
      return 'primary';
    case 'destructive':
      return 'primary';
    case 'outline':
      return 'default';
    case 'secondary':
      return 'default';
    case 'ghost':
      return 'text';
    case 'link':
      return 'link';
    default:
      return 'primary';
  }
};

// 映射 size 到 antd size
const getAntButtonSize = (size: ButtonSize): AntButtonProps['size'] => {
  switch (size) {
    case 'sm':
      return 'small';
    case 'lg':
      return 'large';
    case 'icon':
    case 'default':
    default:
      return 'middle';
  }
};

// variant 对应的额外样式
const variantStyles: Record<ButtonVariant, string> = {
  default: 'shadow-sm',
  destructive:
    'bg-destructive hover:bg-destructive/90 border-destructive shadow-sm',
  outline:
    'bg-white border-border hover:bg-gray-50 hover:border-border-strong text-foreground shadow-sm',
  secondary:
    'bg-secondary text-secondary-foreground hover:bg-secondary/80 border-secondary shadow-sm',
  ghost:
    'bg-transparent border-transparent hover:bg-gray-100 shadow-none text-foreground [&_svg]:text-current',
  link: 'bg-transparent border-transparent shadow-none p-0 h-auto text-primary hover:text-primary/80',
};

// size 对应的额外样式
const sizeStyles: Record<ButtonSize, string> = {
  default: 'h-9 px-4 py-2',
  sm: 'h-8 px-3 text-xs',
  lg: 'h-11 px-6',
  icon: 'h-9 w-9 p-0',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const antType = getAntButtonType(variant);
    const antSize = getAntButtonSize(size);
    const isDestructive = variant === 'destructive';

    return (
      <AntButton
        ref={ref}
        type={antType}
        size={antSize}
        danger={isDestructive}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-all',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        style={style}
        {...props}
      >
        {children}
      </AntButton>
    );
  },
);

Button.displayName = 'Button';

export { Button };
